//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import CultureCategoryModel from './cultureCategory.model';

/**
 * @service CultureCategoryService
 * @description Service for managing culture category data operations.
 * Provides HTTP methods for retrieving culture categories from the backend API.
 * Culture categories define the type of aquaculture being practiced (e.g., Shrimp, Fish, Hatchery).
 * Extends base Service class with generic type CultureCategoryModel.
 *
 * @feature 1. Fetch all culture categories with pagination
 * @feature 2. Cancel ongoing HTTP requests to prevent memory leaks
 * @feature 3. Promise-based async operations for culture category management
 *
 * @architecture
 * Extends base Service<CultureCategoryModel> providing REST API integration with configurable base URL
 * and service endpoint. Implements request tracking for cleanup and cancellation. Uses HttpClient
 * for HTTP operations with standard error handling.
 *
 * @service-endpoint /aqu/culturecategory/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class CultureCategoryService extends Service<CultureCategoryModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/culturecategory/');
  }

  /**
   * @method getAllCultureCategories
   * @async
   * @description Fetch all culture category records from the backend
   *
   * Makes HTTP GET request to backend endpoint /aqu/culturecategory/
   * with query parameters for page size (2000) and order (new). Wraps response in Promise for
   * async/await compatibility.
   *
   * @returns {Promise<Array<CultureCategoryModel>>} Promise resolving to array of CultureCategoryModel objects
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const categories = await cultureCategoryService.getAllCultureCategories();
   * console.log(categories); // Array of CultureCategoryModel objects
   */
  public getAllCultureCategories() {
    return new Promise<Array<CultureCategoryModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<CultureCategoryModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.cultureCategoryService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
